// Chore mini-games (ECONOMY.md §1.5). Locked decision: every mini-game IS a
// husbandry task, and the chore stays completable without playing one — the
// game only speeds it up or adds a bonus. Daily caps keep the anti-addiction
// policy from GAME-DESIGN.md §5 intact.

import type { Breed, GameState, StoredEgg } from '../core/types';
import { feedTypeForStage } from './lifecycle';
import { calendarDayKey } from './time';

export type MinigameId = 'egg_wash' | 'feed_mix';

export interface MinigameDefinition {
  id: MinigameId;
  /** What real skill it teaches — the reason it is allowed to exist. */
  teaches: string;
  maxReward: number;
  playsPerDay: number;
}

export const MINIGAMES: Record<MinigameId, MinigameDefinition> = {
  egg_wash: {
    id: 'egg_wash',
    teaches: 'candling-lite: spotting cracks and grading eggs by size',
    maxReward: 5,
    playsPerDay: 1,
  },
  feed_mix: {
    id: 'feed_mix',
    teaches: 'matching the ration to the flock’s youngest bird',
    maxReward: 10,
    playsPerDay: 1,
  },
};

export function playsToday(state: GameState, id: MinigameId, day = calendarDayKey()): number {
  const log = state.minigames?.[id];
  return log?.day === day ? log.plays : 0;
}

export function canPlay(state: GameState, id: MinigameId, day = calendarDayKey()): boolean {
  return playsToday(state, id, day) < MINIGAMES[id].playsPerDay;
}

function recordPlay(state: GameState, id: MinigameId, day: string): void {
  state.minigames = { ...(state.minigames ?? {}), [id]: { day, plays: playsToday(state, id, day) + 1 } };
}

// --- Egg Wash & Grade ---

export type EggGrade = 'S' | 'M' | 'L' | 'XL';
export const EGG_GRADES: EggGrade[] = ['S', 'M', 'L', 'XL'];

export interface EggWashRound {
  eggs: Array<{ id: string; size: EggGrade; cracked: boolean }>;
}

/**
 * Builds a round from eggs actually sitting in the nest boxes, so the game is
 * about the player's own eggs rather than an abstract puzzle.
 */
export function buildEggWashRound(eggs: StoredEgg[]): EggWashRound {
  return {
    eggs: eggs.slice(0, 6).map((egg, index) => ({
      id: egg.id,
      size: egg.size,
      // Deterministic from the egg id: the same egg is never cracked twice.
      cracked: hashCode(egg.id + index) % 7 === 0,
    })),
  };
}

export interface EggWashResult {
  correct: number;
  total: number;
  reward: number;
}

/**
 * Scores the sort. Grading a cracked egg as sellable is the mistake the game
 * exists to teach, so it simply counts as wrong.
 */
export function scoreEggWash(
  state: GameState,
  round: EggWashRound,
  answers: Record<string, EggGrade | 'cracked'>,
  day = calendarDayKey(),
): EggWashResult {
  if (!canPlay(state, 'egg_wash', day)) {
    return { correct: 0, total: round.eggs.length, reward: 0 };
  }
  let correct = 0;
  for (const egg of round.eggs) {
    const answer = answers[egg.id];
    if (egg.cracked ? answer === 'cracked' : answer === egg.size) correct++;
  }
  const total = round.eggs.length;
  const reward = total === 0 ? 0 : Math.round((correct / total) * MINIGAMES.egg_wash.maxReward);
  state.eggits += reward;
  recordPlay(state, 'egg_wash', day);
  return { correct, total, reward };
}

// --- Feed Mix ---

export type FeedChoice = 'starter' | 'grower' | 'layer';

/**
 * The right answer is the ration the YOUNGEST bird needs, because layer feed's
 * calcium damages growing kidneys (clay-farm-science NUT-04). Feeding the whole
 * flock down to its youngest member is the real rule.
 */
export function correctFeedFor(state: GameState): FeedChoice {
  const stages = state.flock.filter((c) => c.alive).map((c) => feedTypeForStage(c.stage));
  if (stages.includes('starter')) return 'starter';
  if (stages.includes('grower')) return 'grower';
  return 'layer';
}

export interface FeedMixResult {
  correct: boolean;
  reward: number;
  explanation: string;
}

export function scoreFeedMix(
  state: GameState,
  choice: FeedChoice,
  day = calendarDayKey(),
): FeedMixResult {
  const answer = correctFeedFor(state);
  const correct = choice === answer;
  if (!canPlay(state, 'feed_mix', day)) {
    return { correct, reward: 0, explanation: answer };
  }
  const reward = correct ? MINIGAMES.feed_mix.maxReward : 0;
  state.eggits += reward;
  recordPlay(state, 'feed_mix', day);
  return { correct, reward, explanation: answer };
}

/** Which breeds are in the flock — shown as the hint in the Feed Mix game. */
export function flockBreeds(state: GameState): Breed[] {
  return [...new Set(state.flock.map((c) => c.breed))];
}

function hashCode(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash * 31 + value.charCodeAt(i)) | 0;
  }
  return Math.abs(hash);
}
